import React, { useState, useEffect } from "react";
import { Requirement } from "@/types/project";
import { RequirementType } from "./RequirementType";
import { RequirementStatusBadge } from "./RequirementStatusBadge";
import { DocumentIndicator } from "./DocumentIndicator";
import { RequirementActions } from "./RequirementActions";
import { InlineDocumentList } from "./InlineDocumentList";
import { Loader2 } from "lucide-react";
import { useDocumentService } from "@/hooks/useDocumentService";

interface RequirementRowProps {
  requirement: Requirement;
  status: string;
  isStatusLoading: boolean;
  documentCounts: any[];
  isDocumentLoading: boolean;
  onViewGuidance: (requirement: Requirement) => void;
  onUploadDocumentation: (requirement: Requirement) => void;
  onMarkNotApplicable: (requirement: Requirement) => void;
  onMarkReadyForReview?: (requirement: Requirement) => void;
  onMarkIncomplete?: (requirement: Requirement) => void;
  onStatusChange?: (requirement: Requirement, status: string) => void;
}

export function RequirementRow({
  requirement,
  status,
  isStatusLoading,
  documentCounts,
  isDocumentLoading,
  onViewGuidance,
  onUploadDocumentation,
  onMarkNotApplicable,
  onMarkReadyForReview,
  onMarkIncomplete,
  onStatusChange
}: RequirementRowProps) {
  const requirementId = requirement.requirement_id || requirement.id;
  const [documents, setDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasDocuments, setHasDocuments] = useState(false);
  const documentService = useDocumentService();
  
  useEffect(() => {
    // Check if this requirement has any documents based on documentCounts
    const docInfo = documentCounts.find(d => d.requirementId === requirementId);
    setHasDocuments(!!docInfo && docInfo.count > 0);
  }, [requirementId, documentCounts]);

  useEffect(() => {
    // Only fetch documents if we expanded the row and there are documents
    if (isExpanded && hasDocuments) {
      fetchDocuments();
    }
  }, [isExpanded, hasDocuments, requirementId]);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      
      // Extract project ID from document counts if available
      const docInfo = documentCounts.find(d => d.requirementId === requirementId);
      if (!docInfo || !docInfo.projectId) {
        console.log("No document info or project ID found for requirement:", requirementId);
        setDocuments([]);
        return;
      }
      
      const docs = await documentService.fetchDocumentsForRequirement(requirementId, docInfo.projectId);
      setDocuments(docs);
    } catch (error) {
      console.error("Error fetching document details:", error);
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDocuments = () => {
    setIsExpanded(!isExpanded);
  };

  const docInfo = documentCounts.find(d => d.requirementId === requirementId);
  const hasDocumentsToShow = !!docInfo && docInfo.count > 0;

  return (
    <tr className="border-b hover:bg-muted/50">
      <td className="py-3 px-4">
        {requirement.section || <span className="text-muted-foreground text-xs">-</span>}
      </td>
      <td className="py-3 px-4">
        {requirement.subsection || <span className="text-muted-foreground text-xs">-</span>}
      </td>
      <td className="py-3 px-4">
        <div>
          {requirement.requirement_text || requirement.text || <span className="text-muted-foreground text-xs">-</span>}
          
          {hasDocumentsToShow && (
            <InlineDocumentList 
              documents={documents}
              isExpanded={isExpanded}
              onToggle={toggleDocuments}
              isLoading={isLoading}
            />
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <RequirementType type={requirement.requirement_type || requirement.type} />
      </td>
      <td className="py-3 px-4">
        {isStatusLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RequirementStatusBadge 
            status={status} 
            requirement={requirement}
            onStatusChange={onStatusChange}
            clickable={!!onStatusChange}
          />
        )}
      </td>
      <td className="py-3 px-4">
        <div 
          className={`cursor-pointer ${hasDocumentsToShow ? 'hover:opacity-80' : ''}`} 
          onClick={hasDocumentsToShow ? toggleDocuments : undefined}
        >
          <DocumentIndicator 
            documentCounts={documentCounts} 
            requirementId={requirementId} 
            isLoading={isDocumentLoading} 
          />
        </div>
      </td>
      <td className="py-3 px-4">
        <RequirementActions 
          requirement={requirement}
          onViewGuidance={onViewGuidance}
          onUploadDocumentation={onUploadDocumentation}
        />
      </td>
    </tr>
  );
}
